import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import {
  ChevronDown,
  Columns2,
  Plus,
  Rows2,
  Square,
  SquareTerminal,
  X,
  type LucideIcon,
} from "lucide-react";
import { ContextMenu } from "radix-ui";
import {
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../api/queries.js";
import { useAssistantStatus } from "../assistant/api.js";
import { useConsolePower } from "../pages/server/console/power.js";
import { OsIcon } from "../ui/os-icon.js";
import { Menu, Sheet } from "../ui/overlay.js";
import { KindIcon, StatusDot, useReconnectConfirm } from "../ui/session.js";
import { growFromTabNext } from "./minimise.js";
import { sessionLevel } from "../assistant/access.js";
import { levelChoices, mayPair, PairBadge, useSessionAccess } from "./pair.js";
import { TerminalPicker } from "./picker.js";
import {
  closeOtherSessions,
  closeSession,
  disconnectSession,
  reconnectSession,
} from "./sessions.js";
import {
  kindOf,
  sessionState,
  sessionTitle,
  tabLook,
  terminals,
  useTerminals,
  type Layout,
  type TabLook,
  type TerminalTab,
} from "./store.js";

const WORKSPACE = "/terminals";
/** A press that moves less than this is a click, not a drag. */
const DRAG_THRESHOLD_PX = 6;
/** The hidden words that tell how to move a tab with the keyboard. */
const MOVE_HINT = "sessions-move-hint";

/** Whether the person may open a session in some project (owners and administrators). */
function useMayOpen(): boolean {
  const projects = useProjects();
  return (projects.data ?? []).some((p) => p.access === "owner" || p.access === "admin");
}

/** The words of a tab for screen readers and its tooltip: name, kind, status, minimised. */
function useTabWords(tab: TerminalTab, look: TabLook) {
  const { t } = useTranslation();
  const state = sessionState(tab);
  const status = t(`sessions.state.${state}`);
  const kind = t(`sessions.kind.${kindOf(tab)}`);
  const title = sessionTitle(tab);
  const minimised = look === "minimised" ? t("sessions.minimised") : null;
  const paired = tab.pair?.on ? t("sessions.pair.on") : null;
  const label = t(tab.unseen ? "sessions.tabNameUnseen" : "sessions.tabName", {
    name: title,
    kind,
    status,
  });
  return {
    state,
    status,
    title,
    label: [label, paired, minimised].filter(Boolean).join(", "),
    tooltip: [title, kind, status, paired, minimised].filter(Boolean).join(" · "),
  };
}

/**
 * The open tab is filled with the accent; the minimised one (the window
 * folded into it) only has a thin accent outline, so it reads as "parked
 * here, still alive"; the others are plain (docs/terminal.md, "Session window").
 */
const LOOK_CLASS: Record<TabLook, string> = {
  open: "border-accent bg-accent-soft text-accent-strong",
  minimised: "border-accent/60 hover:bg-surface-2",
  idle: "border-line hover:bg-surface-2",
};

/**
 * A tab's close button: 24 px with a mouse (WCAG 2.5.8), and on a touch
 * screen the tab's full height with a 44 px hit area around it (an `::after`
 * that reaches 6 px past the button, into the gap between tabs), so a finger
 * finds it while the tab looks the same. The strip is 44 px high on touch
 * screens for that area to fit (`STRIP`).
 */
const CLOSE_BUTTON = clsx(
  "relative mr-1 flex shrink-0 items-center justify-center rounded p-[5px] text-muted hover:bg-surface-2 hover:text-text",
  "pointer-coarse:mr-0 pointer-coarse:size-8 pointer-coarse:p-0",
  "pointer-coarse:after:absolute pointer-coarse:after:-inset-1.5 pointer-coarse:after:content-['']",
);
const STRIP =
  "flex min-w-0 items-center gap-1.5 overflow-x-auto py-1 [scrollbar-width:thin] pointer-coarse:py-1.5";

const MENU_ITEM =
  "flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-text outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-surface-2";

/** Right click (or a long press) on a tab: move, reconnect, disconnect, close others, server page. */
function TabMenu({
  tab,
  index,
  count,
  children,
}: {
  tab: TerminalTab;
  index: number;
  count: number;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = useAssistantStatus();
  const access = useSessionAccess();
  const pairable = mayPair(tab, status.data);
  const level = sessionLevel(tab, status.data);
  const power = useConsolePower(tab);
  const reconnect = useReconnectConfirm(
    { kind: kindOf(tab), serverName: tab.serverName, state: sessionState(tab) },
    () => reconnectSession(tab.id),
  );
  const item = (label: string, onSelect: () => void, disabled = false) => (
    <ContextMenu.Item className={MENU_ITEM} disabled={disabled} onSelect={onSelect}>
      {label}
    </ContextMenu.Item>
  );
  return (
    <>
      {access.dialog}
      {reconnect.dialog}
      {power.element}
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content
            collisionPadding={8}
            className="z-50 min-w-52 rounded-xl border border-line bg-surface p-1 shadow-lg"
          >
            {item(t("sessions.menu.moveLeft"), () => terminals.move(tab.id, -1), index === 0)}
            {item(
              t("sessions.menu.moveRight"),
              () => terminals.move(tab.id, 1),
              index === count - 1,
            )}
            <ContextMenu.Separator className="my-1 h-px bg-line" />
            {item(t("sessions.tools.reconnect"), () =>
              // After the menu has closed and given the focus back: the question takes it then.
              setTimeout(reconnect.request),
            )}
            {item(
              t("sessions.tools.disconnect"),
              () => disconnectSession(tab.id),
              tab.status !== "connected",
            )}
            {power.items.length > 0 && (
              <>
                <ContextMenu.Separator className="my-1 h-px bg-line" />
                <ContextMenu.Label className="px-3 pb-1 pt-1.5 text-xs font-medium text-muted">
                  {t("sessions.power.menu")}
                </ContextMenu.Label>
                {power.items.map((entry) => (
                  <ContextMenu.Item
                    key={entry.label}
                    className={
                      entry.danger ? MENU_ITEM.replace("text-text", "text-danger") : MENU_ITEM
                    }
                    disabled={entry.disabled ?? false}
                    // After the menu has closed: a confirmation takes the focus then.
                    onSelect={() => setTimeout(entry.onSelect)}
                  >
                    <span className="flex flex-col">
                      <span>{entry.label}</span>
                      {entry.hint && <span className="text-xs text-muted">{entry.hint}</span>}
                    </span>
                  </ContextMenu.Item>
                ))}
              </>
            )}
            {pairable && (
              <>
                <ContextMenu.Separator className="my-1 h-px bg-line" />
                <ContextMenu.Label className="px-3 pb-1 pt-1.5 text-xs font-medium text-muted">
                  {t("assistant.access.title")}
                </ContextMenu.Label>
                <ContextMenu.RadioGroup
                  aria-label={t("assistant.access.title")}
                  value={level}
                  onValueChange={(next) => void access.choose(tab, next as typeof level)}
                >
                  {levelChoices(tab, status.data).map((choice) => (
                    <ContextMenu.RadioItem
                      key={choice.level}
                      value={choice.level}
                      disabled={choice.disabled || access.working}
                      title={
                        choice.disabled
                          ? t("assistant.access.aboveMax")
                          : t(`assistant.access.hints.${choice.level}`)
                      }
                      className={MENU_ITEM}
                    >
                      <span
                        aria-hidden
                        className={clsx(
                          "size-2 shrink-0 rounded-full",
                          level === choice.level ? "bg-accent" : "border border-line",
                        )}
                      />
                      {t(`assistant.access.levels.${choice.level}`)}
                    </ContextMenu.RadioItem>
                  ))}
                </ContextMenu.RadioGroup>
                <ContextMenu.Separator className="my-1 h-px bg-line" />
              </>
            )}
            {item(
              t("sessions.menu.serverPage"),
              () =>
                void navigate({
                  to: "/projects/$projectId/servers/$serverId",
                  params: { projectId: tab.projectId, serverId: tab.serverId },
                }),
            )}
            <ContextMenu.Separator className="my-1 h-px bg-line" />
            {item(t("sessions.menu.closeOthers"), () => closeOtherSessions(tab.id), count < 2)}
            {item(t("sessions.close", { name: sessionTitle(tab) }), () => closeSession(tab.id))}
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>
    </>
  );
}

function TabButton({
  tab,
  index,
  count,
  look,
  onOpen,
  onMoved,
  dragging = false,
  dragHandlers,
  wide = false,
}: {
  tab: TerminalTab;
  index: number;
  count: number;
  look: TabLook;
  onOpen: () => void;
  /** Announces the new place after a keyboard move. */
  onMoved?: (tab: TerminalTab, position: number) => void;
  dragging?: boolean;
  dragHandlers?: {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
    onClickCapture: (event: MouseEvent<HTMLDivElement>) => void;
  };
  wide?: boolean;
}) {
  const { t } = useTranslation();
  const words = useTabWords(tab, look);
  // Ctrl+Shift+←/→ (⌘+Shift on a Mac) moves the focused tab, like browser tabs.
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!(event.ctrlKey || event.metaKey) || !event.shiftKey) return;
    const delta = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const to = index + delta;
    if (to < 0 || to >= count) return;
    terminals.move(tab.id, delta);
    onMoved?.(tab, to + 1);
  };
  return (
    <TabMenu tab={tab} index={index} count={count}>
      <div
        data-tab={tab.id}
        data-look={look}
        {...dragHandlers}
        className={clsx(
          "group flex shrink-0 select-none items-center rounded-lg border text-sm",
          LOOK_CLASS[look],
          dragging && "opacity-60 shadow-lg",
          wide && "w-full",
        )}
      >
        <button
          type="button"
          onClick={onOpen}
          onKeyDown={onKeyDown}
          aria-current={look === "open" ? "page" : undefined}
          aria-label={words.label}
          aria-keyshortcuts="Control+Shift+ArrowLeft Control+Shift+ArrowRight"
          aria-describedby={MOVE_HINT}
          title={words.tooltip}
          className={clsx(
            "flex min-h-8 min-w-0 items-center gap-1.5 py-1 pl-2.5 pr-1",
            wide && "flex-1",
          )}
        >
          <OsIcon flavor={tab.osFlavor} version={tab.osVersion} className="size-4" decorative />
          <KindIcon kind={kindOf(tab)} className="size-3.5 text-muted" />
          <span className={clsx("truncate", wide ? "flex-1 text-left" : "max-w-36")}>
            {tab.serverName}
          </span>
          <StatusDot state={words.state} />
          <PairBadge tab={tab} />
          {tab.unseen && (
            <span className="size-1.5 shrink-0 rounded-full bg-accent" title={t("sessions.unseen")}>
              <span className="sr-only">{t("sessions.unseen")}</span>
            </span>
          )}
        </button>
        <button
          type="button"
          data-close
          onClick={() => closeSession(tab.id)}
          aria-label={t("sessions.close", { name: words.title })}
          title={t("sessions.close", { name: words.title })}
          className={CLOSE_BUTTON}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    </TabMenu>
  );
}

/**
 * Dragging tabs like browser tabs: a press that moves 6 px picks the tab up
 * and drops it where the pointer is; a press that does not is a click. A
 * terminal tab dropped onto a pane of the split view goes into that pane.
 */
function useTabDrag(strip: RefObject<HTMLDivElement | null>) {
  const press = useRef<{ id: string; x: number; y: number; moved: boolean } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const handlers = (id: string) => ({
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0 || (event.target as Element).closest("[data-close]")) return;
      press.current = { id, x: event.clientX, y: event.clientY, moved: false };
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      const current = press.current;
      if (!current) return;
      if (!current.moved) {
        const distance = Math.max(
          Math.abs(event.clientX - current.x),
          Math.abs(event.clientY - current.y),
        );
        if (distance < DRAG_THRESHOLD_PX) return;
        current.moved = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(id);
      }
      // Below the strip the tab is on its way to a pane: the order stays.
      const bar = strip.current?.getBoundingClientRect();
      if (bar && event.clientY > bar.bottom + DRAG_THRESHOLD_PX) return;
      // The new place: after every other tab whose middle is left of the pointer.
      const others = [...(strip.current?.querySelectorAll<HTMLElement>("[data-tab]") ?? [])].filter(
        (element) => element.dataset.tab !== id,
      );
      const index = others.filter((element) => {
        const box = element.getBoundingClientRect();
        return box.left + box.width / 2 < event.clientX;
      }).length;
      terminals.moveTo(id, index);
    },
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
      // Dropped onto a pane of the split view: that pane shows the tab.
      if (press.current?.moved) {
        const slot = document
          .elementFromPoint(event.clientX, event.clientY)
          ?.closest<HTMLElement>("[data-pane-slot]")?.dataset.paneSlot;
        if (slot) terminals.placeIn(id, slot);
      }
      setDragging(null);
      // The click that ends a drag must not open the tab; `onClickCapture` drops it.
      setTimeout(() => {
        press.current = null;
      });
    },
    onClickCapture: (event: MouseEvent<HTMLDivElement>) => {
      if (press.current?.moved) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
  });
  return { dragging, handlers };
}

const LAYOUTS: readonly { value: Layout; icon: LucideIcon }[] = [
  { value: "single", icon: Square },
  { value: "columns", icon: Columns2 },
  { value: "rows", icon: Rows2 },
];

/**
 * A segment of the split control: 28 px inside a group with a 2 px inset on
 * every side, so the chosen segment's fill sits exactly inside the border
 * (34 px tall, like the tabs). Radii are concentric (8 px outside, 6 px in);
 * the focus ring is drawn in the inset, not over the border.
 */
const SEGMENT =
  "flex size-7 shrink-0 items-center justify-center rounded-md focus-visible:outline-offset-0";

/**
 * The split view of terminals, between the last tab and «+»: «Один /
 * Рядом / Друг под другом», shown with two or more terminal tabs (split is
 * for terminals). With two panes, a small menu picks the other pane's
 * terminal (or drag its tab onto the pane). Choosing brings the terminals
 * on screen when the workspace is hidden or shows a desktop or console.
 */
function SplitControl({ onShow }: { onShow: (id: string) => void }) {
  const { t } = useTranslation();
  const state = useTerminals();
  const ssh = state.tabs.filter((tab) => kindOf(tab) === "ssh");
  const first = state.panes[0] ?? ssh[0]?.id;
  if (ssh.length < 2 || !first) return null;
  const active = state.tabs.find((tab) => tab.id === state.active);
  const terminalsShown = state.visible && !!active && kindOf(active) === "ssh";
  const focused = terminalsShown && active ? active.id : first;
  const choose = (layout: Layout) => {
    terminals.setLayout(layout);
    if (!terminalsShown) onShow(focused);
  };
  const others = ssh.filter((tab) => tab.id !== focused);
  return (
    <div
      role="group"
      aria-label={t("terminal.workspace.layout")}
      data-split-control
      className="flex shrink-0 items-center gap-0.5 rounded-lg border border-line p-0.5"
    >
      {LAYOUTS.map(({ value, icon: Icon }) => {
        const label = t(`terminal.workspace.${value}`);
        const pressed = state.layout === value;
        return (
          <button
            key={value}
            type="button"
            data-layout={value}
            aria-label={label}
            title={label}
            aria-pressed={pressed}
            onClick={() => choose(value)}
            className={clsx(
              SEGMENT,
              pressed ? "bg-accent-soft text-accent-strong" : "text-muted hover:bg-surface-2",
            )}
          >
            <Icon className="size-4" aria-hidden />
          </button>
        );
      })}
      {state.layout !== "single" && others.length > 1 && (
        <Menu
          align="end"
          trigger={
            <button
              type="button"
              aria-label={t("terminal.workspace.secondMenu")}
              title={t("terminal.workspace.secondMenu")}
              className={clsx(SEGMENT, "w-6 text-muted hover:bg-surface-2")}
            >
              <ChevronDown className="size-3.5" aria-hidden />
            </button>
          }
          groups={[
            others.map((tab) => ({
              label: sessionTitle(tab),
              checked: state.panes.includes(tab.id),
              onSelect: () => {
                terminals.setOther(tab.id);
                if (!terminalsShown) onShow(focused);
              },
            })),
          ]}
        />
      )}
    </div>
  );
}

/**
 * Sessions in the centre of the top bar: one tab per session (OS, kind,
 * server, status, unseen output, close), reordered by dragging,
 * Ctrl+Shift+←/→ or the tab's menu, the split view of terminals, and «+
 * Подключиться к серверу» — alone until a session is open, its words shown
 * only where the bar has room for them. The strip scrolls when there are
 * many: the tabs give way, never the controls beside them. Phones get one
 * icon instead — it opens the server picker, or with sessions open shows
 * their count and opens a sheet with them (no split view there: phones stack
 * panes).
 */
export function TerminalBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const state = useTerminals();
  const mayOpen = useMayOpen();
  const [sheet, setSheet] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const strip = useRef<HTMLDivElement>(null);
  const drag = useTabDrag(strip);
  const { tabs } = state;
  if (!mayOpen && tabs.length === 0) return null;
  const show = (id: string) => {
    // Before the tab's look changes: the workspace grows out of it as it is now.
    growFromTabNext(id);
    terminals.activate(id);
    setSheet(false);
    void navigate({ to: WORKSPACE });
  };
  const moved = (tab: TerminalTab, position: number) =>
    setAnnouncement(t("sessions.moved", { name: sessionTitle(tab), position, count: tabs.length }));
  const connect = (
    <button
      type="button"
      onClick={() => terminals.openPicker()}
      aria-label={tabs.length > 0 ? t("terminal.connect") : undefined}
      title={t("terminal.connect")}
      className="flex min-h-8 shrink-0 items-center gap-1.5 rounded-lg border border-dashed border-line px-2.5 text-sm text-muted hover:border-accent hover:text-text"
    >
      <Plus className="size-4" aria-hidden />
      {tabs.length === 0 && (
        // The centre of the bar is a size container: the words show when they fit.
        <span className="hidden whitespace-nowrap @min-[14rem]:inline">
          {t("terminal.connect")}
        </span>
      )}
    </button>
  );
  const unseen = tabs.some((tab) => tab.unseen);
  const phoneLabel =
    tabs.length === 0 ? t("terminal.connect") : t("sessions.phoneButton", { count: tabs.length });
  return (
    <>
      <nav aria-label={t("sessions.tabsLabel")} className="hidden min-w-0 sm:flex">
        <div ref={strip} data-session-strip className={STRIP}>
          {tabs.map((tab, index) => (
            <TabButton
              key={tab.id}
              tab={tab}
              index={index}
              count={tabs.length}
              look={tabLook(state, tab.id)}
              onOpen={() => show(tab.id)}
              onMoved={moved}
              dragging={drag.dragging === tab.id}
              dragHandlers={drag.handlers(tab.id)}
            />
          ))}
          <SplitControl onShow={show} />
          {connect}
        </div>
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </nav>
      <span id={MOVE_HINT} className="sr-only">
        {t("sessions.moveHint")}
      </span>
      <div className="sm:hidden">
        {/* An icon like its neighbours (assistant, notifications): words overlapped the logo at 320–390 px. */}
        <button
          type="button"
          onClick={() => (tabs.length === 0 ? terminals.openPicker() : setSheet(true))}
          aria-label={phoneLabel}
          title={phoneLabel}
          data-session-button
          className="relative flex shrink-0 items-center justify-center rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-text"
        >
          <SquareTerminal className="size-5" aria-hidden />
          {tabs.length > 0 && (
            <span
              className={clsx(
                "absolute right-0.5 top-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[0.65rem] font-semibold tabular-nums leading-4",
                unseen ? "bg-accent text-white" : "bg-text text-surface",
              )}
              aria-hidden
            >
              {tabs.length > 9 ? "9+" : tabs.length}
            </span>
          )}
        </button>
        <Sheet open={sheet} onOpenChange={setSheet} title={t("sessions.tabsLabel")}>
          <div className="flex flex-col gap-2">
            {tabs.map((tab, index) => (
              <TabButton
                key={tab.id}
                tab={tab}
                index={index}
                count={tabs.length}
                wide
                look={tabLook(state, tab.id)}
                onOpen={() => show(tab.id)}
                onMoved={moved}
              />
            ))}
            <button
              type="button"
              onClick={() => {
                setSheet(false);
                terminals.openPicker();
              }}
              className="flex min-h-10 items-center justify-center gap-2 rounded-lg border border-dashed border-line px-3 text-sm hover:border-accent"
            >
              <Plus className="size-4" aria-hidden />
              {t("terminal.connect")}
            </button>
          </div>
        </Sheet>
      </div>
      {state.picker && <TerminalPicker request={state.picker} />}
    </>
  );
}
